import { useCallback, useEffect, useState } from 'react'
import { useAsync } from 'react-async'
import styled, { css } from 'styled-components'

import { OtpWithMode } from '@root/api/models/Otp'
import { OtpService } from '@root/api/services/OtpService'
import { useConfig } from '@root/Context'
import { SessionExpired } from '@root/routes/OPSM'

import { Modal } from '@components/Modal'
import ModalStyle from './OTPModalStyling'

import OPSMIcon from '../../assets/icons/opsm-logo.svg'

import { useHandleAppointmentError } from '@components/GlobalErrorModal/useHandleAppointmentError'
import { useGetCurrentLanguage } from '@hooks/useLanguage'
import { ApiRequestOptions } from '@root/api/core/ApiRequestOptions'
import { ApiResult } from '@root/api/core/ApiResult'
import { useEventBus } from '@root/context/EventProvider'
import { isCaOrUs } from '@root/utils/utils'
import { anzIntro } from '@utils/routes'
import { useHistory } from 'react-router'
import { ErrorModal } from './OTPErrorModal'
import StepOne from './StepOne'
import StepTwo from './StepTwo'

const StyledContainer = styled.div`
  padding: 3rem 1.25rem;

  ${({ theme }) => css`
    ${theme.breakpoints.media.small} {
      padding: 3rem 1.75rem;
    }

    ${theme.breakpoints.media.medium} {
      padding: 3rem 2.25rem;
    }
  `}
`

export const DEFAULT_RESEND_COUNTDOWN = 60

const OTPModal = () => {
  const [activeStep, setActiveStep] = useState<number>(0)
  const [otpSentOptions, setOtpSentOptions] = useState<OtpWithMode>()
  const [isOtpInvalidated, setOtpInvalidated] = useState<boolean>(false)

  const {
    brand,
    appointmentId,
    caller,
    subscriptionKey: ocpApimSubscriptionKey,
    country,
  } = useConfig()

  const currentLanguage = useGetCurrentLanguage()

  const history = useHistory()

  const getContacts = useCallback(
    () =>
      OtpService.getCensoredContacts({
        brand,
        appointmentId,
        ocpApimSubscriptionKey,
        caller,
        acceptedLanguage: currentLanguage,
      }),
    [appointmentId, brand, caller, currentLanguage, ocpApimSubscriptionKey]
  )

  const { isPending, data, error } = useAsync(getContacts)

  const navigateToStepOne = () => setActiveStep(0)

  const navigateToStepTwo = (otpResponse: OtpWithMode) => {
    setOtpSentOptions(otpResponse)
    setActiveStep(1)
  }

  const navigateToStep = (otpInvalidated = false) => {
    if (otpInvalidated) {
      navigateToStepOne()
      return
    }
    history.push(anzIntro())
  }

  const handleOtpInvalidation = () => {
    setOtpInvalidated(true)
    navigateToStepOne()
  }

  const [apiAppointmentError, setApiAppointmentError] = useState<ApiResult>()

  const { subscribe } = useEventBus()
  const { errorMessage } = useHandleAppointmentError(apiAppointmentError)

  const handleSetApiAppointmentError = useCallback(
    ({
      result,
      options,
    }: {
      result: ApiResult
      options: ApiRequestOptions
    }) => {
      // endpoint should match the following pattern: appointment/:appointmentId/otp/censoredContacts
      const matchesEndpointCensoredContacts = options.path.match(
        /\/form\/appointment\/[a-zA-Z0-9+]+\/otp\/censoredContacts/
      )
      if (result && matchesEndpointCensoredContacts) {
        setApiAppointmentError(result)
      }
    },
    []
  )

  useEffect(
    () => subscribe('api-error', handleSetApiAppointmentError),
    [subscribe, handleSetApiAppointmentError]
  )

  if (apiAppointmentError)
    return (
      <ErrorModal
        isOpen
        error={error}
        errorMessage={errorMessage}
        showBackToTheFormBtn={false}
      />
    )

  if (!isCaOrUs(country) && error) {
    return <SessionExpired type="country" />
  }

  return (
    <Modal isOpen>
      <StyledContainer>
        {!isCaOrUs(country) ? (
          <ModalStyle.StyledHeader>
            <img src={OPSMIcon} alt="Logo" />
          </ModalStyle.StyledHeader>
        ) : null}
        {activeStep === 0 ? (
          <StepOne
            navigateToStepTwo={navigateToStepTwo}
            contacts={data}
            isLoading={isPending}
            otpInvalidated={isOtpInvalidated}
          />
        ) : null}
        {activeStep === 1 ? (
          <StepTwo
            otpOptions={otpSentOptions}
            navigateToStep={navigateToStep}
            handleOtpInvalidation={handleOtpInvalidation}
          />
        ) : null}
      </StyledContainer>
    </Modal>
  )
}

export default OTPModal
